import { FormEvent, useContext, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import AuthContext from "../context/AuthContext";
import { IDevice } from "../types/deviceInterface";
import { isLteDevice } from "../utils/deviceUtils";
import useAxios from "../utils/useAxios";

const measurementOptions = [5, 10, 15, 30, 60, 120];
const transmissionOptions = [30, 60, 120, 240, 360, 480];

const DeviceSettingsView = () => {
  const { id } = useParams<{ id: string }>();
  const { userSettings } = useContext(AuthContext);
  const isAdmin = userSettings?.user_type === "ADMIN";
  const api = useAxios();
  const toast = useToast();
  const background = useColorModeValue("white", "gray.900");
  const [device, setDevice] = useState<IDevice>();
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [measurement, setMeasurement] = useState("");
  const [transmission, setTransmission] = useState("");
  const [immediate, setImmediate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isQueued, setIsQueued] = useState(false);

  useEffect(() => {
    let active = true;
    setDevice(undefined);
    setIsLoading(true);
    setLoadFailed(false);
    setIsQueued(false);
    setMeasurement("");
    setTransmission("");
    setImmediate(false);

    api.get<IDevice>(`frontend/devices/current-data/${id}/`)
      .then(({ data }) => {
        if (active) setDevice(data);
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
    // useAxios creates a new client on each render; reload only when the device changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const measurementMinutes = Number(measurement);
    const transmissionMinutes = immediate ? 0 : Number(transmission);
    const validMeasurement =
      Number.isInteger(measurementMinutes) &&
      (isAdmin
        ? measurementMinutes >= 1
        : measurementOptions.includes(measurementMinutes));
    const validTransmission =
      immediate ||
      (Number.isInteger(transmissionMinutes) &&
        (isAdmin
          ? transmissionMinutes >= 1
          : transmissionOptions.includes(transmissionMinutes)));

    if (!id || !isLteDevice(device) || !validMeasurement || !validTransmission) {
      toast({
        title: <Trans>Choose valid intervals within the allowed ranges.</Trans>,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    setIsQueued(false);
    try {
      await api.post(`interface/sensors/${id}/set-transmission-interval/`, {
        "Measurement-interval": measurementMinutes,
        "Transmission-interval": transmissionMinutes,
      });
      setIsQueued(true);
    } catch {
      toast({
        title: <Trans>Unable to queue device settings. Please try again.</Trans>,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Navbar>
      <Container maxW="container.md" py={6}>
        <VStack align="stretch" spacing={5}>
          <Button as={Link} to={`/devices/${id}`} alignSelf="flex-start" variant="outline" borderRadius={0}>
            <Trans>Back to device</Trans>
          </Button>
          <Heading size="lg"><Trans>Device settings</Trans></Heading>
          {isLoading ? (
            <Spinner />
          ) : loadFailed ? (
            <Alert status="error"><AlertIcon /><Trans>Error fetching data</Trans></Alert>
          ) : !isLteDevice(device) ? (
            <Alert status="warning">
              <AlertIcon />
              <Trans>Device settings are only available for LTE-M devices.</Trans>
            </Alert>
          ) : (
            <>
              <Text>{device?.device_name || <Trans>Device #{id}</Trans>}</Text>
              <Box bg={background} p={{ base: 4, md: 6 }} borderWidth="1px">
                <VStack as="form" align="stretch" spacing={5} onSubmit={handleSubmit} onChange={() => setIsQueued(false)}>
                  <Heading size="md"><Trans>Measurement and transmission</Trans></Heading>
                  <Text>
                    <Trans>
                      The default measurement interval is 15 minutes, with transmission every 2 hours.
                      This typically provides 4 years of battery life, depending on conditions.
                    </Trans>
                  </Text>
                  <Alert status="warning">
                    <AlertIcon />
                    <Trans>More frequent transmissions will shorten battery life.</Trans>
                  </Alert>
                  <Text fontSize="sm">
                    <Trans>Changes take effect the next time the device communicates, not immediately.</Trans>
                  </Text>
                  <FormControl isRequired isDisabled={isSaving}>
                    <FormLabel htmlFor="measurement-interval"><Trans>Measurement interval (minutes)</Trans></FormLabel>
                    {isAdmin ? (
                      <Input id="measurement-interval" type="number" min={1} step={1} placeholder={t`Select interval`}
                        value={measurement} onChange={(event) => setMeasurement(event.target.value)} />
                    ) : (
                      <Select id="measurement-interval" placeholder={t`Select interval`} value={measurement} onChange={(event) => setMeasurement(event.target.value)}>
                        {measurementOptions.map((minutes) => <option key={minutes} value={minutes}>{minutes}</option>)}
                      </Select>
                    )}
                  </FormControl>
                  <Checkbox isChecked={immediate} isDisabled={isSaving} onChange={(event) => setImmediate(event.target.checked)}>
                    <Trans>Send each measurement immediately</Trans>
                  </Checkbox>
                  <FormControl isRequired={!immediate} isDisabled={immediate || isSaving}>
                    <FormLabel htmlFor="transmission-interval"><Trans>Transmission interval (minutes)</Trans></FormLabel>
                    {isAdmin ? (
                      <Input id="transmission-interval" type="number" min={1} step={1} placeholder={t`Select interval`}
                        value={transmission} onChange={(event) => setTransmission(event.target.value)} />
                    ) : (
                      <Select id="transmission-interval" placeholder={t`Select interval`} value={transmission} onChange={(event) => setTransmission(event.target.value)}>
                        {transmissionOptions.map((minutes) => <option key={minutes} value={minutes}>{minutes}</option>)}
                      </Select>
                    )}
                  </FormControl>
                  {isQueued && (
                    <Alert status="success">
                      <AlertIcon />
                      <Trans>Settings queued. They will be applied the next time the device communicates.</Trans>
                    </Alert>
                  )}
                  <Stack direction={{ base: "column", sm: "row" }}>
                    <Button type="submit" colorScheme="green" borderRadius={0} isLoading={isSaving}>
                      <Trans>Save settings</Trans>
                    </Button>
                  </Stack>
                </VStack>
              </Box>
            </>
          )}
        </VStack>
      </Container>
    </Navbar>
  );
};

export default DeviceSettingsView;
